import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Observable, of, catchError, map, take } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';

import { Image, ImageType, Occ, OccConfig, Page, Price, Product, VariantOption } from '@spartacus/core';

import {
  BrandsEnum, DataTableColumnTypeEnum, IBenefit, IMedia, IProductCard, IProductClassification, IProductCustomer,
  IProductFeature, IProductFeatures, IPromo, IReparability, ITag, ReinsurancesPdpType, ThemesEnum
} from '@seb/web-design-system/core';

import { IProductLinkCard } from '@seb/web-design-system/product-link-card';
import { IProductSeller } from '@seb/web-design-system/product-seller';
import { IOverview } from '@seb/web-design-system/overview';
import { IReview } from '@seb/web-design-system/reviews';

import {
  BrandsCorner, CustomClassification, CustomFeature, ProductBenefit, ReinsuranceFooterModuleInterface,
  ServiceOptionsComponentInterface, VerifiedReview
} from '../models';

import { BadgeEnum, ExternalLogisticianEnum, StockStatusEnum } from '../enums';
import { UrlMappingService } from '../services/url-mapping.service';
import { BaseStoreService } from '../services/base-store.service';
import { StockService } from '../services/stock.service';
import { ImageService } from '../services/image.service';
import { LinkMapper } from './link.mapper';
import { HobMapper } from './hob.mapper';

@Injectable({
  providedIn: 'root'
})
export class ProductMapper {
  constructor(
    private translateService: TranslateService,
    private imageService: ImageService,
    private occConfig: OccConfig,
    private urlMappingService: UrlMappingService,
    private baseStoreService: BaseStoreService,
    private linkMapper: LinkMapper,
    private readonly hobMapper: HobMapper,
    private readonly stockService: StockService,
    @Inject(PLATFORM_ID) protected platformId: any
  ) {}

  productToProductCard(product: Product, disableProductComparator?: boolean): Observable<IProductCard> {
    const brandFound = this.baseStoreService.getBrand(product.brand);
    const productUrl = LinkMapper.decodeUrl(product.url);
    const productRefsSAV = product?.afterSalesServiceReferences ? product?.afterSalesServiceReferences : undefined;

    return this.urlMappingService.getUrlMappingByCode(productUrl).pipe(
      take(1),
      catchError(() => of(undefined)),
      map((urlMapping) => {
        const baseStore = this.baseStoreService.baseStore;
        const isReviewActivated = baseStore.ratingAndReviewsIdWebsite;
        const isNewPriceManagementActivated = baseStore.activateNewPriceManagement;
        let finalProductUrl = productUrl;
        if (urlMapping) {
          finalProductUrl = productUrl.replace(urlMapping.code, urlMapping.value);
        }
        const _activeBrands = baseStore.houseOfBrandConfig?.brand;
        const isHob = _activeBrands?.length > 0;
        const prices = {
          price: product.price,
          discountedPrice: product.discountedPrice,
          recommendedPrice: product.recommendedPrice,
          strikethroughPrice: product.strikethroughPrice
        };
        const media = this.imageService.normalizeMedia(this.getMedia(product.primaryImages));

        return {
          ...this.formatCardPrice(prices, isNewPriceManagementActivated),
          media: media ?? product.images?.[ImageType.PRIMARY]?.['main'] ?? product.images?.[ImageType.PRIMARY]?.['thumbnail'],
          title: product.name,
          subtitle: product.claim,
          cmmf: product.commercialCode,
          code: product.code,
          benefits: this.mapTopBenefits(product.topBenefits).slice(0, 2),
          isAccessory: product.isAccessory,
          stock: {
            online: this.isInStock(product),
            lowStock: this.stockService.checkStock(product?.price?.value, product?.stock?.stockLevel)
          },
          tags: this.getProductTags(product, isNewPriceManagementActivated, isHob) || [],
          isProductComparator: disableProductComparator ? false : product.isProductComparator,
          ratings: isReviewActivated
            ? {
              averageRatings: product?.gsebAverageRating
                ? Number(Number(product.gsebAverageRating).toFixed(1))
                : undefined,
              numberOfReviews: product.gsebNumberOfReviews,
              url: finalProductUrl + '#block-product-reviews'
            }
            : undefined,
          isInFavorite: false,
          url: this.linkMapper.getBrandUrl(product.brand, finalProductUrl),
          seller: this.getSeller(product.externalLogistician),
          onSale: product.onSale,
          ecoTax:
            product.ecoTaxPrice?.value && product.price?.formattedValue
              ? product.ecoTaxPrice.formattedValue
              : undefined,
          indexFollow: product?.indexFollow,
          reparabilityData: product?.repairabilityIndex?.value
            ? { index: parseFloat(product?.repairabilityIndex?.value.replace(',', '.')) }
            : undefined,
          madeInData: this.getBadge(product),
          refurbishedLogo: this.getRefurbishedLogo(product),
          brand: brandFound ? product?.brand : BrandsEnum.default,
          brandLogo: brandFound ? { media: brandFound?.logo } : undefined,
          brandLogoTitle: isHob && !product?.isAccessory ? this.hobMapper.getBrandLogoTitle(product.brand) : undefined,
          isTaxesIncluded: baseStore?.displayTaxesIncluded,
          refSAV: productRefsSAV ? this.formatRefSAV(productRefsSAV) : undefined,
          productGenre: product?.productGenre
        };
      })
    );
  }

  productToProductCustomer(product: Product): Observable<IProductCustomer> {
    return this.productToProductCard(product).pipe(
      map((productCard) => {
        return {
          ...productCard,
          cmmf: product.code,
          code: product.commercialCode,
          urlCsp: product.urlCsp
        };
      })
    );
  }

  getProductTags(product: Product, isNewPriceManagementActivated: boolean, isHob?: boolean): ITag[] {
    const tags: ITag[] = [];

    const addTag = (text: string, theme: ThemesEnum, isDark = false) => {
      tags.push({ text, theme, isDark });
    };

    const variant = this.getProductVariant(product);
    const isRefurbished = !!variant
                           && (variant.code === undefined || product.code === variant.code);

    if (isRefurbished) {
      addTag(this.translateService.instant('pdp.refurbished.badge.name'), isHob ? ThemesEnum.promo : ThemesEnum.success, isHob);
      addTag(this.translateService.instant('pdp.refurbished.old.label'), isHob ? ThemesEnum.promo : ThemesEnum.lightSuccess, isHob);
    }

    if (product.newProduct) {
      addTag(this.translateService.instant('product.tags.newProduct'), isHob ? ThemesEnum.promo : ThemesEnum.stars, isHob);
    }

    if (product.bestSeller) {
      addTag(this.translateService.instant('product.tags.bestSeller'), ThemesEnum.promo, isHob);
    }

    if (product.formattedDiscountValue && (product.strikethroughPrice || isNewPriceManagementActivated)) {
      addTag(`-${product.formattedDiscountValue}`, ThemesEnum.promo2);
    }

    const promoText = this.extractPromoText(product.promoMessage);
    if (product.promoMessageEnabled && promoText) {
      addTag(promoText, isHob ? ThemesEnum.promo : ThemesEnum.extra, isHob);
    }

    return tags.slice(0, 3);
  }

  private extractPromoText(promoMessage?: string): string | undefined {
    if (!promoMessage || !promoMessage.includes('<promotion>') || !promoMessage.includes('</promotion>')) {
      return undefined;
    }
    return promoMessage.split('<promotion>', 2)[1]?.split('</promotion>', 2)[0]?.trim() || undefined;
  }

  emptyProductCard(code?: string): IProductCard {
    return { code, isInFavorite: false, media: {}, url: `/p/${code}`, title: code };
  }

  mapTopBenefits(benefits: ProductBenefit[]): IBenefit[] {
    if (!benefits) {
      return [];
    }
    return benefits
      .filter((benefit) => !benefit.hideBenefit)
      .map((benefit) => ({
        description: benefit.shortText,
        media: benefit.hideMedia ? undefined : this.imageService.normalizeMedia(benefit.picto)
      }));
  }

  mapBenefits(benefits: ProductBenefit[]): IBenefit[] {
    if (!benefits) {
      return [];
    }
    return benefits
      .filter((benefit) => !benefit.hideBenefit)
      .map((benefit) => ({
        description: benefit.description,
        media: benefit.hideMedia ? undefined : this.imageService.normalizeMedia(benefit.image)
      }));
  }

  classificationToProductClassification(
    classifications: CustomClassification[],
    isFeatures = false
  ): IProductClassification[] {
    return (
      classifications
        ?.filter((classification) => !!classification && (!isFeatures || classification.type !== 'USER_FRIENDLY'))
        ?.map((classification) => ({
          name: classification.name,
          code: classification.code,
          features: isFeatures
            ? this.featureToProductTableFeature(classification.features)
            : this.featureToProductFeature(classification.features)
        })) || []
    );
  }

  featureToProductTableFeature(features: CustomFeature[]): any[] {
    return features
      .filter((feature) => !!feature)
      .map((feature) => ({
        description: feature.name,
        featureValues: feature.featureValues,
        featureUnit: feature.featureUnit
      }));
  }

  featureToProductFeature(features: CustomFeature[]): IProductFeature[] {
    return features
      .filter((feature) => !!feature)
      .map((feature) => ({
        type: DataTableColumnTypeEnum.media,
        comparable: feature.comparable,
        name: feature.name,
        range: feature.range,
        description: feature.description,
        code: feature.code,
        featureUnit: feature.featureUnit,
        featureValues: feature.featureValues,
        media: this.imageService.normalizeMedia(feature.picto)
      }));
  }

  getProductFeatures(product, pdpFeatures): IProductFeatures | undefined {
    try {
      const sizeGuideName = this.translateService.instant('pdp.sizeGuide.name');

      if (product?.isAccessory || product?.isBundle) {
        return undefined;
      }

      if (!sizeGuideName || sizeGuideName.trim() === '' || sizeGuideName === 'pdp.sizeGuide.name') {
        return undefined;
      }

      if (!pdpFeatures || !pdpFeatures?.sizeGuide?.level1Families || !pdpFeatures?.sizeGuide?.level2Families) {
        return undefined;
      }

      const globalLevel1Families = pdpFeatures?.sizeGuide?.level1Families;
      const globalLevel2Families = pdpFeatures?.sizeGuide?.level2Families;

      if (
        !globalLevel1Families?.includes(product?.familyAnneeN) ||
        !globalLevel2Families?.includes(product?.family2)
      ) {
        return undefined;
      }

      const sizesData = this.translateService.instant('pdp.sizeGuide.sizes');

      if (!sizesData || sizesData.trim() === '' || sizesData === 'pdp.sizeGuide.sizes') {
        return undefined;
      }

      return {
        title: this.translateService.instant('pdp.sizeGuide.diameterAndPortions'),
        linkName: sizeGuideName,
        titleLevel: 3,
        modalFeatures: {
          title: sizeGuideName,
          description: this.translateService.instant('pdp.sizeGuide.description'),
          subtitle: this.translateService.instant('pdp.sizeGuide.diameterAndPortions'),
          features: (() => {
            const sizesArray = typeof sizesData === 'string' ? JSON.parse(sizesData) : sizesData;
            return sizesArray.map((size) => ({
              diameter: size.diameter,
              libelle: this.translateService.instant(size.libelle),
              icon: 'icon-diameter'
            }));
          })()
        }
      };
    } catch (error) {
      console.error('Error in getProductFeatures:', error);
      return undefined;
    }
  }

  productToIOverview(
    product: Product,
    currentPage?: Page,
    routerState?: any,
    brandFound?: BrandsCorner,
    isTaxesIncluded?: boolean,
    cmpReinsurancesServicesAndWarranty?: ReinsuranceFooterModuleInterface,
    cmpPaymentModesForProduct?: ServiceOptionsComponentInterface,
    isForfait?: boolean,
    isServicePage?: boolean,
    isNewPriceManagementActivated?: boolean,
    pdpFeatures?: any,
    isHob?: boolean,
    iAdvizeId?: string
  ): IOverview {
    const hasDiscountedPrice =
      !!product.price?.value && !!product.discountedPrice?.value
        ? product.discountedPrice?.value !== product.price?.value
        : product.discountedPrice?.formattedValue !== product.price?.formattedValue;
    const hasRecommendedPrice =
      !!product.price?.value && !!product.recommendedPrice?.value
        ? product.recommendedPrice?.value !== product.price?.value
        : product.recommendedPrice?.formattedValue !== product.price?.formattedValue;
    const features = product.benefitHighLights || [];
    const _reinsurancesServicesPdp = cmpReinsurancesServicesAndWarranty?.componentsObjects?.length > 0
      ? this.getReinsurancesServicesPdp(cmpReinsurancesServicesAndWarranty.componentsObjects)
      : undefined;
    const _reinsurancesPdp = this.getReinsurancesPdp(product, cmpPaymentModesForProduct, _reinsurancesServicesPdp);
    const productRefsSAV = isServicePage && product?.afterSalesServiceReferences ? product?.afterSalesServiceReferences : undefined;
    const shouldDisplayVariant = (!!this.getProductVariant(product) || product?.baseProduct) && product?.onSale === true;
    return {
      productLinkCard: shouldDisplayVariant ? this.getLinkCardByStatus(product, isNewPriceManagementActivated) : undefined,
      maxQty: product.maxOrderQuantity,
      tags: this.getProductTags(product, isNewPriceManagementActivated, isHob),
      title: currentPage?.['h1Tag']
        ? currentPage['h1Tag']
        : routerState?.state?.params['param0'] === 'instructions-for-use'
          ? `${this.translateService.instant('SEO.noticeTitle')} ${product.name}`
          : product.name,
      code: product.commercialCode,
      subtitle: product.claim,
      features,
      price: product.price?.value > 0 ? product.price?.formattedValue : undefined,
      discountedPrice:
        !!product.discountedPrice && hasDiscountedPrice ? product.discountedPrice?.formattedValue : undefined,
      recommendedPrice:
        !!product.recommendedPrice && hasRecommendedPrice && isNewPriceManagementActivated ? product.recommendedPrice?.formattedValue : undefined,
      isDiscountWithRecommendedPrice: !isNewPriceManagementActivated ? !product.strikethroughPrice : false,
      hasLocalStore: false,
      stock: {
        online: this.isInStock(product),
        store: this.isInStock(product),
        lowStock: this.stockService.checkStock(product?.price?.value, product?.stock?.stockLevel),
        releaseDate: !isForfait ? product?.stock?.releaseDate : undefined
      },
      ecoTax:
        product.ecoTaxPrice?.value > 0 && product.price?.formattedValue
          ? product.ecoTaxPrice.formattedValue
          : undefined,
      promo: product.promoMessageEnabled ? this.getProductPromo(product) : undefined,
      promoQualiRepar:
        product?.potentialPromotions && product?.potentialPromotions.length > 0 && isForfait
          ? this.getProductPromoQualiRepar(product)
          : undefined,
      quantity: 0,
      isInFavorite: false,
      description: { label: '', title: '', description: product.shortDescription },
      benefits: this.mapTopBenefits(product.topBenefits),
      benefitsUrl: 'benefits-list',
      isProductComparator: false,
      store: undefined,
      onSale: product.onSale,
      compatibleProduct:
        !!product.isAccessory && !!product.productsDesignedFor
          ? {
            active: product.isAccessory,
            nbCompatibleProduct: product.productsDesignedFor?.length
          }
          : undefined,
      dividedData: product?.dividedData,
      reparabilityData: this.getRepairability(product),
      brandLogo: brandFound && !product.isAccessory ? { media: brandFound?.logo } : undefined,
      isTaxesIncluded: isTaxesIncluded,
      productFeatures: this.getProductFeatures(product, pdpFeatures),
      productId: product.commercialCode.slice(0, 5),
      reinsurancesPdp: _reinsurancesPdp?.length > 0 ? _reinsurancesPdp : undefined,
      soldBy: this.getSoldBy(product),
      seeBenefits: this.translateService.instant('product.seeBenefits'),
      shipping: product.isAccessory
        ? this.translateService.instant('accessory.shipping')
        : this.translateService.instant('product.shipping'),
      refSAV: productRefsSAV ? this.formatRefSAV(productRefsSAV) : undefined,
      iAdvizeId
    };
  }

  formatRefSAV(productRefsSAV: string[]) {
    const refSAVWording = this.translateService.instant('product.reference.afterSalesService');
    return productRefsSAV.map((str, index) => index === 0 ? refSAVWording + ': ' + str : str);
  }

  private getReinsurancesServicesPdp(reinsurances) {
    const reinsurancesServicesPdp = [];
    reinsurances.forEach((reinsurance) => {
      const splitHeadline = reinsurance.headline.split('</b>');
      const title = splitHeadline[0] + '</b>';
      const subtitle = splitHeadline[1];
      const iconName = reinsurance.ReinsuranceType.toLowerCase();

      reinsurancesServicesPdp.push({
        title,
        info: subtitle,
        icon: { url: `/assets/images/${iconName}.png` },
        description: '',
        type: reinsurance.ReinsuranceType
      });
    });

    return reinsurancesServicesPdp;
  }

  getReinsurancesPdp(product: Product, cmpPaymentModesForProduct: ServiceOptionsComponentInterface, reinsurancesServicesPdp) {
    const _reinsurancesPdp = [];
    const isForfait = product.productGenre === 'Forfait';
    let securedPayementReinsurance = this.setReinsurancePdp('', ReinsurancesPdpType.securedPayement, false);
    securedPayementReinsurance = this.mapPaymentModesLogos(securedPayementReinsurance, cmpPaymentModesForProduct);
    _reinsurancesPdp.push(securedPayementReinsurance);

    if (!isForfait) {
      const pfs = product.externalLogistician === ExternalLogisticianEnum.PFS ? '.pfs' : '';

      _reinsurancesPdp.push(this.setReinsurancePdp(pfs, ReinsurancesPdpType.freeDelivery, product.isAccessory));
      _reinsurancesPdp.push(this.setReinsurancePdp('', ReinsurancesPdpType.freeReturn, product.isAccessory));
    }

    if (reinsurancesServicesPdp) {
      reinsurancesServicesPdp.forEach((reinsuranceServicesPdp) => _reinsurancesPdp.push(reinsuranceServicesPdp));
    }

    return _reinsurancesPdp.filter((reinsurancePdp) => reinsurancePdp);
  }

  mapPaymentModesLogos(securedPayementReinsurance: any, cmpPaymentModesForProduct: ServiceOptionsComponentInterface) {
    return securedPayementReinsurance ? {
      ...securedPayementReinsurance,
      description: cmpPaymentModesForProduct?.medias ? this.buildLogos(cmpPaymentModesForProduct.medias) : securedPayementReinsurance.descriptionModal
    } : securedPayementReinsurance;
  }

  private buildLogos(medias: { [index: string]: IMedia }) {
    return Object.keys(medias)
      .map((key) => {
        const media = this.imageService.normalizeMedia(medias[key]);
        return `<img width="48px" height="28px" src="${media.url}">`;
      })
      .join(' ');
  }

  setReinsurancePdp(pfs, reinsurance, isAccessory) {
    let reinsurancePdp = undefined;
    const prefix = isAccessory ? 'accessory' : 'product';
    const objTranslations = this.translateService.instant([
      `${prefix}.reinsurance.${reinsurance}.title`,
      `${prefix}.reinsurance.${reinsurance}.icon`,
      `${prefix}${pfs}.reinsurance.${reinsurance}.info`,
      `${prefix}.reinsurance.${reinsurance}.descriptionModal`
    ]);

    if (objTranslations) {
      reinsurancePdp = {
        title:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.title'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.title'] !==
            prefix + '.reinsurance.' + reinsurance + '.title'
            ? objTranslations[prefix + '.reinsurance.' + reinsurance + '.title']
            : undefined,
        icon:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon'] !==
            prefix + '.reinsurance.' + reinsurance + '.icon'
            ? {
              url: objTranslations[prefix + '.reinsurance.' + reinsurance + '.icon']
            }
            : undefined,
        info:
          objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info'] &&
          objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info'] !==
            prefix + pfs + '.reinsurance.' + reinsurance + '.info'
            ? objTranslations[prefix + pfs + '.reinsurance.' + reinsurance + '.info']
            : undefined,
        description:
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal'] &&
          objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal'] !==
            prefix + '.reinsurance.' + reinsurance + '.descriptionModal'
            ? objTranslations[prefix + '.reinsurance.' + reinsurance + '.descriptionModal']
            : undefined,
        type: reinsurance
      };
    }
    return reinsurancePdp?.title ? reinsurancePdp : undefined;
  }

  getProductPromoQualiRepar(product: Product): IPromo {
    const qualiReparPromo = product?.potentialPromotions.filter(
      (promo) => promo?.ruleGroup === 'qualiReparRuleGroup'
    )[0];
    const objTranslations = this.translateService.instant([
      'product.qualiRepar.banner.logo',
      'product.qualiRepar.banner.text',
      'product.qualiRepar.popin.logo',
      'product.qualiRepar.popin.title',
      'product.qualiRepar.popin.text',
      'product.qualiRepar.popin.close'
    ]);
    const promoQualiRepar: IPromo = {
      text: qualiReparPromo?.description,
      info:
        objTranslations['product.qualiRepar.banner.text'] &&
        objTranslations['product.qualiRepar.banner.text'] !== 'product.qualiRepar.banner.text'
          ? objTranslations['product.qualiRepar.banner.text']
          : undefined,
      media:
        objTranslations['product.qualiRepar.banner.logo'] &&
        objTranslations['product.qualiRepar.banner.logo'] !== 'product.qualiRepar.banner.logo'
          ? {
            altText: '',
            url: objTranslations['product.qualiRepar.banner.logo'],
            mime: 'image'
          }
          : undefined,
      modal: {
        title:
          objTranslations['product.qualiRepar.popin.title'] &&
          objTranslations['product.qualiRepar.popin.title'] !== 'product.qualiRepar.popin.title'
            ? objTranslations['product.qualiRepar.popin.title']
            : undefined,
        description:
          objTranslations['product.qualiRepar.popin.text'] &&
          objTranslations['product.qualiRepar.popin.text'] !== 'product.qualiRepar.popin.text'
            ? objTranslations['product.qualiRepar.popin.text']
            : undefined,
        media:
          objTranslations['product.qualiRepar.popin.logo'] &&
          objTranslations['product.qualiRepar.popin.logo'] !== 'product.qualiRepar.popin.logo'
            ? {
              altText: '',
              url: objTranslations['product.qualiRepar.popin.logo'],
              mime: 'image'
            }
            : undefined,
        button:
          objTranslations['product.qualiRepar.popin.close'] &&
          objTranslations['product.qualiRepar.popin.close'] !== 'product.qualiRepar.popin.close'
            ? objTranslations['product.qualiRepar.popin.close']
            : undefined
      }
    };
    return promoQualiRepar;
  }

  getProductPromo(product: Product): IPromo {
    let iPromo: IPromo;
    const promo = product.promoMessage?.split('<coupon>', 2)[1]?.split('</coupon>', 2)[0];
    const text = product.promoMessage?.split('</coupon>')[1] || product.promoMessage?.split('</promotion>')[1];
    if (text) {
      iPromo = { text };
    }
    if (promo) {
      iPromo = {
        promo,
        ...iPromo
      };
    }
    return iPromo;
  }

  getSeller(externalLogistician: ExternalLogisticianEnum): IProductSeller {
    return (externalLogistician === ExternalLogisticianEnum.PFS) ? {
      text: this.translateService.instant('product.shopLogistician')
    } : {
      text: this.translateService.instant('product.sellerLogistician')
    };
  }

  isInStock(product: Product): boolean {
    return (
      product?.stock?.stockLevelStatus === StockStatusEnum.IN_STOCK ||
      product?.stock?.stockLevelStatus === StockStatusEnum.LOW_STOCK
    );
  }

  toIfuUrl(product: Occ.Product): string {
    const ifuUrl = product.url.replace('/p/', '/csp/');
    return '/instructions-for-use' + ifuUrl;
  }

  toIReviews(reviews: VerifiedReview, langIsoCode: string): IReview[] {
    const isoCode = langIsoCode.replace('_', '-');
    return reviews.reviews.map((review) => ({
      id: review.id_review_product,
      rating: {
        averageRatings: parseFloat(review.rate),
        numberOfReviews: 1
      },
      content: review.review,
      author: `${review.lastname} ${review.firstname}`,
      creationDate: new Date(review.publish_date.replace(/-/g, '/')).toLocaleDateString(isoCode),
      purchasedDate: new Date(review.order_date.replace(/-/g, '/')).toLocaleDateString(isoCode),
      vote: {
        positive: {
          value: parseInt(review.count_helpful_yes, 10),
          voted: false
        },
        negative: {
          value: parseInt(review.count_helpful_no, 10),
          voted: false
        }
      }
    }));
  }

  getRepairability(product: Product): IReparability {
    if (product?.repairabilityIndex) {
      const url = product.repairabilityIndex.calculation?.url.trim();
      return {
        index: parseFloat(product.repairabilityIndex.value.replace(',', '.')),
        link: {
          url: '/repair-documents'
        },
        calculateLink: {
          url: new RegExp(/^(http|\/\/)/i).test(url)
            ? url
            : `${this.occConfig?.backend?.occ?.baseUrl}${url.startsWith('/') ? url : `/${url}`}`,
          external: true,
          linkName: product.repairabilityIndex.calculation?.mediaTitle
        }
      };
    }
    return undefined;
  }

  getBadge(product: Product): IMedia {
    const badgeMedia = product?.badges?.find(
      (productBadge) => !!productBadge && productBadge?.badge === BadgeEnum.MADEIN
    );
    return this.imageService.normalizeMedia(badgeMedia?.media);
  }

  formatCardPrice(
    prices: { price?: Price, discountedPrice?: Price, recommendedPrice?: Price, strikethroughPrice?: boolean },
    isNewPriceManagementActivated: boolean
  ) {
    return {
      price: prices?.price?.formattedValue,
      discountedPrice:
        !!prices?.discountedPrice && prices?.discountedPrice.value !== prices?.price.value
          ? prices?.discountedPrice?.formattedValue
          : undefined,
      recommendedPrice: !!prices?.recommendedPrice && prices?.recommendedPrice.value !== prices?.price.value && isNewPriceManagementActivated
        ? prices?.recommendedPrice?.formattedValue
        : undefined,
      isDiscountWithRecommendedPrice: !isNewPriceManagementActivated ? !prices?.strikethroughPrice : false
    };
  }

  getLinkCardByStatus(product: Product, isNewPriceManagementActivated): IProductLinkCard {
    const isRefurbished = product.baseProduct && product.baseProduct !== product.code;
    const targetProduct = this.getProductVariant(product)?.product;
    const theme: ThemesEnum = isRefurbished ? ThemesEnum.light : ThemesEnum.default;
    const translationKey = isRefurbished ? 'old' : 'new';
    const prices = {
      price: targetProduct?.price,
      discountedPrice: targetProduct?.discountedPrice,
      recommendedPrice: targetProduct?.recommendedPrice,
      strikethroughPrice: targetProduct?.strikethroughPrice
    };

    return {
      icon: isRefurbished ? 'icon-new' : 'icon-refurbished',
      link: { url: targetProduct?.url, linkName: this.translateService.instant('pdp.refurbished.' + translationKey + '.buy') },
      title: this.translateService.instant('pdp.refurbished.' + translationKey + '.title'),
      description: this.translateService.instant('pdp.refurbished.' + translationKey + '.text'),
      ...this.formatCardPrice(prices, isNewPriceManagementActivated),
      theme
    };
  }

  getSoldBy(product: Product): string {
    if (product.isAccessory) {
      return product.externalLogistician === ExternalLogisticianEnum.PFS
        ? this.translateService.instant('accessory.pfs.soldBy')
        : this.translateService.instant('accessory.soldBy');
    }
    else {
      return product.externalLogistician === ExternalLogisticianEnum.PFS
        ? this.translateService.instant('product.pfs.soldBy')
        : this.translateService.instant('product.soldBy');
    }
  }

  getRefurbishedLogo(product: Product): IMedia | undefined {
    const logoUrl = this.translateService.instant('pdp.refurbished.logo');
    const variant = this.getProductVariant(product);
    const isRefurbished = variant && (variant.code === undefined || product.code === variant.code);

    return (isRefurbished && logoUrl) ? {
      altText: 'refurbished-logo',
      url: logoUrl
    } : undefined;
  }

  getProductVariant(product: Product): VariantOption {
    return product?.variantOptions
      ?.find((variant) => variant?.variantOptionQualifiers?.findIndex((q) => q.type === 'REFURBISHED' && q.value !== 'NEW') !== -1);
  }

  getMedia(images: Image[]): Image | undefined {

    if (!images) {
      return undefined;
    }

    let primary, thumbnail;

    for (const image of images) {
      if (image.format === 'main') {
        return image;
      }
      if (image.format === 'thumbnail') {
        thumbnail = image;
      }
    }

    return primary ?? thumbnail;
  }
}
